import firebase from 'firebase';

import { NIMBLY_API_USER_PUSH_TOKEN } from '../constants/api';

/**
 * Clears the signed-in user's push token through api-users.
 *
 * ADR-069. This replaces a direct `firebase.set('/userPushToken/<uid>', '')` at both logout
 * paths. That RTDB write had no Mongo counterpart, and the notification service now reads the
 * token from Mongo — so once the read flipped, a web logout stopped suppressing push for that
 * user while the RTDB node it cleared was no longer consulted by anything.
 *
 * An empty token is written rather than a row deleted, because that is exactly what the RTDB
 * write did and api-users has no delete route. The store treats the two identically:
 * `findManyByUserIDs` skips falsy tokens, so the notification service reports the user as
 * having no token registered.
 *
 * Failure-tolerant, mirroring `./loggedDevice`: both call sites are logout paths, and throwing
 * here would leave the user half signed out. A failure leaves the previous token in place,
 * which means one more notification to a device the user just signed out of — worse than
 * clearing, better than a broken logout.
 */
export const clearPushToken = async (): Promise<void> => {
  try {
    const currentUser = firebase.auth().currentUser;
    if (!currentUser) {
      // eslint-disable-next-line no-console
      console.warn('[pushToken] no currentUser; skipping push token clear');
      return;
    }

    const authToken = await currentUser.getIdToken();
    const response = await fetch(NIMBLY_API_USER_PUSH_TOKEN, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        authorization: authToken
      },
      body: JSON.stringify({ pushToken: '' })
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body?.error || body?.message || `push token clear failed (${response.status})`);
    }
  } catch (error) {
    // Not rethrown -- see above -- but not silent either: a token that fails to clear is how a
    // signed-out user keeps receiving notifications with nothing in the logs to explain it.
    // eslint-disable-next-line no-console
    console.error('[pushToken] push token clear failed', error);
  }
};
